import { useEffect, useRef } from 'react';
import { FilterGroup } from 'components/filter/types';
import { useFiltersCache } from 'contexts/useFiltersCache';
import { FilterCacheKey } from 'types/filter-cache-key';

const useInitFilters = (
    cacheKey: FilterCacheKey,
    defaultFiltersConsumer: FilterGroup[],
    shouldGarbageCollect = true,
) => {
    const defaultFilters = useRef(defaultFiltersConsumer);
    const cacheKeyRef = useRef(cacheKey);
    const {
        updateFilter: updateFilterRaw,
        setSegmentId: setSegmentIdRaw,
        isDirty,
    } = useFiltersCache(state => ({
        updateFilter: state.updateFiltersNextByKey,
        setIsDirty: state.setDirtyByKey,
        isDirty: !!state.filters[cacheKeyRef.current]?.dirty,
        setSegmentId: state.setSegmentIdByKey,
    }));
    const updateFilter = useRef(updateFilterRaw);
    const setSegmentId = useRef(setSegmentIdRaw);

    useEffect(() => {
        if (!isDirty && defaultFilters.current) {
            updateFilter.current(cacheKeyRef.current, defaultFilters.current as any);
            setSegmentId.current(cacheKeyRef.current);
        }
    }, [isDirty]);

    useEffect(() => {
        if (defaultFiltersConsumer) {
            defaultFilters.current = defaultFiltersConsumer;
        }
    }, [defaultFiltersConsumer]);

    useGarbageCollectFilters(cacheKeyRef.current, shouldGarbageCollect);
};

const useGarbageCollectFilters = (cacheKey: FilterCacheKey, shouldGarbageCollect = true) => {
    const { setIsDirty } = useFiltersCache(state => ({
        setIsDirty: state.setDirtyByKey,
    }));

    useEffect(() => {
        return () => {
            if (shouldGarbageCollect) {
                setIsDirty(cacheKey, false);
            }
        };
    }, [cacheKey, setIsDirty, shouldGarbageCollect]);
};

export { useInitFilters, useGarbageCollectFilters };
